/* eslint-disable jsx-a11y/media-has-caption */
import type { HTMLProps } from "react"
import React, { useEffect, useRef, useState } from "react"
import type { VideoJsPlayerOptions } from "video.js"
import type { ChakraProps } from "@chakra-ui/react"
import { Box } from "@chakra-ui/react"
import type { ImageProps } from "next/image"
import Image from "next/image"
import "video.js/dist/video-js.css"

import { dispatch } from "@shared/utils.events"
import { pauseVideo } from "./VideoPlayer.utils"

type VideoJsPlayer = {
  dispose: () => void
}

interface Props {
  coverImageProps: ImageProps
  options: VideoJsPlayerOptions
  videoProps?: HTMLProps<HTMLVideoElement>
  isVisible?: boolean
  styleProps?: ChakraProps
  videoPosition: string
}

interface PosterProps extends ChakraProps {
  imgProps?: ImageProps
}

const Poster: React.FC<PosterProps> = ({ imgProps, ...otherProps }) => (
  <Box
    position="absolute"
    top="0"
    left="0"
    bottom="0"
    right="0"
    w="100%"
    h="100%"
    cursor="pointer"
    {...otherProps}
  >
    {imgProps?.src && (
      <Image
        style={{ objectFit: imgProps?.style?.objectFit ?? "cover" }}
        title="Video Placeholder"
        {...imgProps}
        fill
        sizes="100vw"
      />
    )}
  </Box>
)

export const VideoPlayer: React.FC<Props> = ({
  options,
  videoProps,
  isVisible = true,
  coverImageProps,
  styleProps,
  videoPosition,
}) => {
  const [isVideoLoaded, setVideoLoaded] = useState(false)
  const [isCoverImageHidden, setCoverImageHidden] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    let player: VideoJsPlayer

    const loadVideo = async () => {
      const VideoJS = (await import("video.js")).default

      if (videoRef.current) {
        player = VideoJS(videoRef.current, options)
      }
      setVideoLoaded(true)
    }

    loadVideo()
    return () => {
      if (player) {
        player.dispose()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (videoRef.current && !isVisible) {
      pauseVideo(videoRef.current)
    }
  }, [isVisible])

  return (
    <>
      <div data-vjs-player>
        <video
          ref={videoRef}
          crossOrigin="anonymous"
          onPlay={() => {
            if (!isCoverImageHidden) setCoverImageHidden(true)
            dispatch("video.onplay", {
              position: videoPosition,
              target: videoRef.current,
            })
          }}
          onPause={() => {
            dispatch("video.onpause", {
              position: videoPosition,
              target: videoRef.current,
            })
          }}
          onEnded={() => {
            dispatch("video.onended", {
              position: videoPosition,
              target: videoRef.current,
            })
          }}
          className="video-js vjs-big-play-centered"
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...videoProps}
          style={{ objectFit: "cover", overflow: "hidden" }}
        />
        {!isCoverImageHidden && <Poster imgProps={coverImageProps} />}
      </div>

      {/* show this placeholder until the video is ready to play */}
      {!isVideoLoaded && (
        <Box
          {...styleProps}
          position="relative"
          css={{
            "&:after": {
              content: "''",
              display: "block",
              paddingBottom: "125%", // 4/5 aspect ratio
            },
          }}
        >
          <Poster imgProps={coverImageProps} />
        </Box>
      )}
    </>
  )
}
